// Lectura de balanzas desde el navegador de la PC de báscula.
//
// El servidor del ERP no puede abrir puertos COM. La lectura se hace:
//   1) vía agente local "AranduJAR Serial Server" (agente_balanza/), o
//   2) vía Web Serial API (Chrome/Edge, solo equipos seriales).
// El agente devuelve la trama CRUDA; la interpretación (parser) es siempre esta,
// para que haya un único lugar donde ajustar formatos.

const AGENT_KEY = "balanza_agent_url";
export const AGENT_URL_DEFAULT = "http://127.0.0.1:8765";

export function getAgentUrl() {
  try { return localStorage.getItem(AGENT_KEY) || AGENT_URL_DEFAULT; } catch { return AGENT_URL_DEFAULT; }
}
export function setAgentUrl(url) {
  try { localStorage.setItem(AGENT_KEY, (url || "").trim() || AGENT_URL_DEFAULT); } catch { /* sin storage */ }
}

export const webSerialDisponible = () => typeof navigator !== "undefined" && "serial" in navigator;

// ── Parser ────────────────────────────────────────────────────────────────────
function hexATexto(hex) {
  const limpio = (hex || "").replace(/[^0-9a-fA-F]/g, "");
  let out = "";
  for (let i = 0; i + 1 < limpio.length; i += 2) out += String.fromCharCode(parseInt(limpio.slice(i, i + 2), 16));
  return out;
}

/**
 * Interpreta una trama y devuelve el peso en KG, o null si no hay un número válido.
 * equipo: { formato_datos, separador, decimales, unidad }
 */
export function parsearTrama(trama, equipo = {}) {
  if (trama == null) return null;
  let txt = String(trama);
  if (equipo.formato_datos === "HEX") txt = hexATexto(txt);
  // quita caracteres de control (STX/ETX/CR/LF) pero conserva el separador si es un control
  const sep = equipo.separador || "";
  txt = txt.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, " ");
  let candidatos = [txt];
  if (sep) {
    const partes = txt.split(sep.replace(/\\r/g, "\r").replace(/\\n/g, "\n").replace(/\\t/g, "\t"));
    if (partes.length > 1) candidatos = partes;
  }
  const re = /[-+]?\d+(?:[.,]\d+)?/g;
  let mejor = null;
  for (const c of candidatos) {
    const m = c.match(re);
    if (!m) continue;
    // el número con más dígitos es el peso (descarta "ST", "GS", contadores cortos)
    for (const n of m) {
      if (!mejor || n.replace(/\D/g, "").length > mejor.replace(/\D/g, "").length) mejor = n;
    }
  }
  if (!mejor) return null;
  const tieneDecimal = /[.,]/.test(mejor);
  let valor = parseFloat(mejor.replace(",", "."));
  if (!Number.isFinite(valor)) return null;
  const dec = Math.max(0, Math.min(3, Number(equipo.decimales) || 0));
  if (!tieneDecimal && dec > 0) valor = valor / Math.pow(10, dec);
  const u = equipo.unidad || "kg";
  if (u === "g") valor /= 1000;
  else if (u === "t") valor *= 1000;
  return Math.round(valor * 1000) / 1000;
}

// ── Agente local ─────────────────────────────────────────────────────────────
export async function agenteSalud(url = getAgentUrl(), timeoutMs = 2500) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(`${url}/health`, { signal: ctl.signal });
    if (!r.ok) return { ok: false, error: `HTTP ${r.status}` };
    return { ok: true, ...(await r.json()) };
  } catch (e) {
    return { ok: false, error: "No se pudo conectar con el agente local" };
  } finally { clearTimeout(t); }
}

async function leerPorAgente(equipo, timeoutMs) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs + 2000);
  let r;
  try {
    r = await fetch(`${getAgentUrl()}/leer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ equipo, timeout_ms: timeoutMs }),
      signal: ctl.signal,
    });
  } catch (e) {
    const err = new Error("AGENTE_NO_DISPONIBLE");
    err.agenteCaido = true;
    throw err;
  } finally { clearTimeout(t); }
  let j = {};
  try { j = await r.json(); } catch { /* respuesta vacía */ }
  if (!r.ok || j.ok === false) throw new Error(j.error || `El agente respondió HTTP ${r.status}`);
  return j.trama;
}

// ── Web Serial ───────────────────────────────────────────────────────────────
let _puerto = null;

async function leerPorWebSerial(equipo, timeoutMs) {
  if (!webSerialDisponible()) throw new Error("Este navegador no soporta Web Serial (use Chrome/Edge o instale el agente local)");
  if (["M", "S"].includes(equipo.serial_parity)) throw new Error("Paridad Mark/Space no soportada por Web Serial; use el agente local");
  if (!_puerto) _puerto = await navigator.serial.requestPort(); // requiere gesto del usuario
  const opts = {
    baudRate: Number(equipo.serial_baud) || 9600,
    dataBits: Number(equipo.serial_data_bits) || 8,
    stopBits: Number(equipo.serial_stop_bits) === 2 ? 2 : 1,
    parity: { N: "none", E: "even", O: "odd" }[equipo.serial_parity] || "none",
    flowControl: equipo.serial_flow_control === "rtscts" ? "hardware" : "none",
  };
  try { await _puerto.open(opts); } catch (e) {
    if (!/already open/i.test(String(e))) { _puerto = null; throw e; }
  }
  const reader = _puerto.readable.getReader();
  const dec = new TextDecoder("latin1");
  let acum = "";
  const fin = Date.now() + timeoutMs;
  try {
    while (Date.now() < fin) {
      const restante = fin - Date.now();
      const r = await Promise.race([
        reader.read(),
        new Promise((res) => setTimeout(() => res({ timeout: true }), restante)),
      ]);
      if (r.timeout || r.done) break;
      acum += dec.decode(r.value, { stream: true });
      // trama completa = termina en salto de línea y trae al menos un dígito
      if (/[\r\n]/.test(acum) && /\d/.test(acum)) break;
    }
  } finally {
    try { await reader.cancel(); } catch { /* ya cerrado */ }
    reader.releaseLock();
    try { await _puerto.close(); } catch { /* ya cerrado */ }
  }
  if (!acum) throw new Error("Timeout: la balanza no envió datos");
  return acum;
}

// ── API pública ──────────────────────────────────────────────────────────────
/**
 * Lee un peso de la balanza configurada.
 * Devuelve { kg, trama, via: 'agente'|'webserial'|'simulado', simulado }.
 * Lanza Error con mensaje legible.
 */
export async function leerPeso(equipo, { timeoutMs = 3000 } = {}) {
  if (!equipo) throw new Error("Seleccione una balanza");
  if (equipo.modo_simulacion) {
    const kg = Math.round(1500 + Math.random() * 28000);
    return { kg, trama: String(kg), via: "simulado", simulado: true };
  }
  let trama;
  let via;
  if (equipo.utilizar_serial_server !== false) {
    try {
      trama = await leerPorAgente(equipo, timeoutMs);
      via = "agente";
    } catch (e) {
      if (!(e.agenteCaido && equipo.modulo !== "MOD_TCP_IP" && webSerialDisponible())) {
        if (e.agenteCaido) throw new Error("No se encontró el agente local de balanza. Inícielo en esta PC (o use Chrome/Edge para conexión directa serial).");
        throw e;
      }
      trama = await leerPorWebSerial(equipo, timeoutMs);
      via = "webserial";
    }
  } else {
    trama = await leerPorWebSerial(equipo, timeoutMs);
    via = "webserial";
  }
  const kg = parsearTrama(trama, equipo);
  if (kg == null) throw new Error(`No se pudo interpretar la trama recibida: ${String(trama).slice(0, 60)}`);
  return { kg, trama, via, simulado: false };
}
